// Rotation entre enfants (TW #17468676). Helpers purs partagés entre le
// service de complétion quotidienne et l'UI du formulaire.
// Cadences : 'day' (un jour par enfant), 'week' (défaut historique, une
// semaine locale par enfant), 'month' (un mois calendaire par enfant).

import { weekWindowFor } from '@/utils/weeklyTarget'

const DAY_MS = 24 * 60 * 60 * 1000
const WEEK_MS = 7 * DAY_MS

export type RotationFrequency = 'day' | 'week' | 'month'

/** Liste ordonnée des enfants de la rotation, [] si la tâche n'est pas en rotation.
 *  Paramètre volontairement `unknown` : rotation_children n'est pas (encore) dans
 *  les types DB générés, comme weekly_target_count. */
export function rotationChildrenOf(task: unknown): string[] {
    const arr = (task as { rotation_children?: string[] | null } | null | undefined)?.rotation_children
    return Array.isArray(arr) ? arr.filter(id => typeof id === 'string' && id.length > 0) : []
}

/** Cadence de rotation de la tâche. NULL/absent/inconnu = 'week' (défaut
 *  historique : les rotations créées avant la colonne étaient hebdo). */
export function rotationFrequencyOf(task: unknown): RotationFrequency {
    const f = (task as { rotation_frequency?: string | null } | null | undefined)?.rotation_frequency
    return f === 'day' || f === 'month' ? f : 'week'
}

/** Parse 'YYYY-MM-DD' à midi UTC ; NaN si malformé. */
function dayAnchorMs(date: string): number {
    return /^\d{4}-\d{2}-\d{2}$/.test(date) ? Date.parse(`${date}T12:00:00Z`) : NaN
}

/** Index de mois calendaire (année*12 + mois) ; NaN si malformé. */
function monthIndexOf(date: string): number {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return NaN
    return Number(date.slice(0, 4)) * 12 + Number(date.slice(5, 7))
}

/**
 * Enfant planifié pour la période (jour, semaine ou mois) contenant `date`.
 * La période contenant `anchorDate` revient à children[0], puis le cycle suit
 * l'ordre du tableau, une période par enfant. Les semaines sont calculées dans
 * la fenêtre locale (`weekStartIndex`, voir localeService.getWeekStartIndex),
 * et une date antérieure à l'ancre reste bien définie (modulo positif).
 * Retourne null si la liste est vide ou une date invalide (une ancre corrompue
 * en DB ne doit pas faire tomber tout le calcul du jour).
 */
export function rotationChildForDate(
    date: string,
    anchorDate: string | null | undefined,
    children: string[],
    weekStartIndex: number,
    frequency: RotationFrequency = 'week'
): string | null {
    if (children.length === 0 || !anchorDate) return null
    let steps: number
    if (frequency === 'day') {
        steps = Math.round((dayAnchorMs(date) - dayAnchorMs(anchorDate)) / DAY_MS)
    } else if (frequency === 'month') {
        steps = monthIndexOf(date) - monthIndexOf(anchorDate)
    } else {
        try {
            // weekWindowFor (via addDays) lève sur une date malformée.
            steps = Math.round(
                (dayAnchorMs(weekWindowFor(date, weekStartIndex).start)
                    - dayAnchorMs(weekWindowFor(anchorDate, weekStartIndex).start)) / WEEK_MS
            )
        } catch {
            return null
        }
    }
    if (Number.isNaN(steps)) return null
    const idx = ((steps % children.length) + children.length) % children.length
    return children[idx] ?? null
}
